import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { LogIn, ArrowLeft } from 'lucide-react';

export default function Login() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      toast.success('Login successful!');
      
      // Check if user is admin and redirect appropriately
      const redirect = searchParams.get('redirect');
      if (redirect) {
        navigate(redirect);
      } else if (user?.role === 'admin' || formData.email.includes('admin')) {
        navigate('/admin/dashboard');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen noise-texture">
      <PremiumHeader />

      <div className="container mx-auto px-4 sm:px-8 py-20">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Previous Page
        </button>

        <div className="max-w-md mx-auto">
          <div className="bg-white border border-border rounded-none p-12">
            <div className="text-center mb-8">
              <LogIn className="w-16 h-16 mx-auto mb-4 text-accent" />
              <h1 className="text-4xl font-black uppercase mb-2">LOGIN</h1>
              <p className="text-sm text-muted-foreground">
                Access your account
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="uppercase tracking-wider text-xs font-bold">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="login-email"
                  required
                  className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                />
              </div>

              <div>
                <Label htmlFor="password" className="uppercase tracking-wider text-xs font-bold">
                  Password
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  data-testid="login-password"
                  required
                  className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                />
              </div>

              <div className="text-right mb-4">
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-primary hover:text-accent font-semibold uppercase tracking-wider"
                >
                  Forgot Password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground disabled:bg-muted disabled:text-muted-foreground rounded-none uppercase tracking-wider font-bold py-6"
              >
                {loading ? 'LOGGING IN...' : 'LOGIN'}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link to="/register" data-testid="goto-register" className="text-accent font-bold hover:underline">
                  Register here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
